/**
 * Payment Hooks
 *
 * Custom hooks for payment-related operations
 */

import { useCallback, useEffect, useState } from "react";
import { paymentService } from "../services";

interface UseSyncReceiptsOptions {
  /** Auto-sync on mount */
  autoSync?: boolean;
  /** Sync interval in milliseconds (0 = no auto-refresh) */
  syncInterval?: number;
}

interface UseSyncReceiptsResult {
  /** Trigger manual sync */
  sync: () => Promise<void>;
  /** Is sync in progress */
  isSyncing: boolean;
  /** Last sync error */
  error: string | null;
  /** Total receipts after last sync */
  totalReceipts: number | null;
  /** Last sync time */
  lastSyncTime: Date | null;
}

/**
 * Hook to sync payment receipts from Google Drive
 *
 * @example
 * ```tsx
 * const { sync, isSyncing } = useSyncReceipts({ autoSync: true });
 * ```
 */
export function useSyncReceipts(
  options: UseSyncReceiptsOptions = {},
): UseSyncReceiptsResult {
  const { autoSync = false, syncInterval = 0 } = options;

  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalReceipts, setTotalReceipts] = useState<number | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  const sync = useCallback(async () => {
    setIsSyncing(true);
    setError(null);

    try {
      const result = await paymentService.syncDriveReceipts();
      setTotalReceipts(result.total_receipts);
      setLastSyncTime(new Date());
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sync receipts";
      setError(errorMessage);
      console.error("Error syncing receipts:", err);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Auto-sync on mount
  useEffect(() => {
    if (autoSync) {
      sync();
    }
  }, [autoSync, sync]);

  // Auto-refresh interval
  useEffect(() => {
    if (syncInterval > 0) {
      const interval = setInterval(sync, syncInterval);
      return () => clearInterval(interval);
    }
  }, [sync, syncInterval]);

  return {
    sync,
    isSyncing,
    error,
    totalReceipts,
    lastSyncTime,
  };
}
